import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, deleteDoc, doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebaseConfig";
import ro from "/src/content/ro.json";
import { toDateNumber } from "../utils/date";

const getTimestamp = (value) => toDateNumber(value) || 0;

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [inscrieri, setInscrieri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsubUsers = onSnapshot(
      collection(db, "users"),
      (snapshot) => {
        const data = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setUsers(data);
        setLoading(false);
      },
      (err) => {
        console.error("Eroare la incarcarea utilizatorilor:", err);
        setError(ro.admin.users.loadError);
        setLoading(false);
      }
    );

    const unsubInscrieri = onSnapshot(
      collection(db, "inscrieri_traseu"),
      (snapshot) => {
        const data = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setInscrieri(data);
      }
    );

    return () => {
      unsubUsers();
      unsubInscrieri();
    };
  }, []);

  const inscrieriByEmail = useMemo(() => {
    const map = new Map();
    inscrieri.forEach((item) => {
      const email = (item.email || item.userEmail || "")
        .toString()
        .trim()
        .toLowerCase();
      if (!email) return;
      const ts =
        getTimestamp(item.createdAt) || getTimestamp(item.dataTraseu) || 0;
      const existing = map.get(email);
      if (!existing || ts >= existing.ts) {
        map.set(email, { ...item, ts });
      }
    });
    return map;
  }, [inscrieri]);

  const handleDelete = async (userId) => {
    const ok = window.confirm(ro.admin.users.deleteConfirm);
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "users", userId));
    } catch (err) {
      console.error("Eroare la stergere utilizator:", err);
      alert(ro.admin.users.deleteError);
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: "var(--color-bg)", color: "var(--color-text)" }}
    >
      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p
              className="text-sm font-semibold"
              style={{ color: "var(--color-accent)" }}
            >
              {ro.admin.users.kicker}
            </p>
            <h1 className="text-3xl font-bold">{ro.admin.users.title}</h1>
            <p
              className="text-sm mt-1"
              style={{ color: "var(--color-secondary-text)" }}
            >
              {ro.admin.users.subtitle}
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg font-semibold border w-full sm:w-auto"
            style={{
              borderColor: "var(--color-border)",
              color: "var(--color-text)",
            }}
          >
            {ro.admin.users.back}
          </button>
        </div>

        {loading && (
          <p style={{ color: "var(--color-secondary-text)" }}>
            {ro.admin.users.loading}
          </p>
        )}
        {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

        <div className="flex items-center justify-between">
          <p
            className="text-sm font-semibold"
            style={{ color: "var(--color-secondary-text)" }}
          >
            {ro.admin.users.total} {users.length}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {!loading && users.length === 0 && (
            <p style={{ color: "var(--color-secondary-text)" }}>
              {ro.admin.users.empty}
            </p>
          )}

          {users.map((user) => {
            const email = user.email || "-";
            const match =
              email !== "-" ? inscrieriByEmail.get(email.toLowerCase()) : null;
            const name = match?.numePrenume || match?.nume || "";
            const phone = match?.telefon || match?.nrTelefon || match?.phone || "";

            return (
              <div
                key={user.id}
                className="rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                style={{
                  backgroundColor: "var(--color-surface)",
                  border: "1px solid var(--color-border)",
                }}
              >
                <div className="flex items-start gap-4 min-w-[220px]">
                  <div className="flex flex-col gap-1">
                    {match ? (
                      <>
                        <p className="font-semibold">
                          {name || ro.admin.users.userFallback}
                        </p>
                        <p
                          className="text-sm"
                          style={{ color: "var(--color-secondary-text)" }}
                        >
                          {email}
                        </p>
                        <p
                          className="text-sm"
                          style={{ color: "var(--color-secondary-text)" }}
                        >
                          {phone || "-"}
                        </p>
                      </>
                    ) : (
                      <>
                        <p
                          className="text-sm"
                          style={{ color: "var(--color-secondary-text)" }}
                        >
                          {email}
                        </p>
                        <p
                          className="text-sm"
                          style={{ color: "var(--color-secondary-text)" }}
                        >
                          {ro.admin.users.noSignup}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => handleDelete(user.id)}
                  className="px-4 py-2 rounded-lg font-semibold w-full sm:w-auto"
                  style={{
                    backgroundColor: "var(--color-danger)",
                    color: "#ffffff",
                  }}
                >
                  {ro.admin.users.deleteLabel}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminUsersPage;
